import { map, catchError } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import * as Globals from '../shared/globals';
import { ConfigService } from '../config/config.service';
import { BlockUIService } from '../shared/services/blockui.service';
import * as CryptoJS from '../crypto-js';
import * as AntiForgeryTokenConfig from '../shared/AntiForgeryTokenConfig';
import { Observable, throwError } from 'rxjs';
import { SessionService } from './services/session.service';
import { Utils } from './utils';

@Injectable()
export class HttpClientService {
    http: HttpClient;
    baseUrl: string;
    isExternalApp: boolean = false;
    key: any;
    iv: any;

    constructor(http: HttpClient, config: ConfigService, private blockUIService: BlockUIService, private sessionService: SessionService) {
        this.http = http;
        this.key = CryptoJS.enc.Utf8.parse(Globals.EncryptionKeys.PasswordEncryptionKey);
        this.iv = CryptoJS.enc.Utf8.parse(Globals.EncryptionKeys.PasswordEncryptionIV);
        let externalAppString = config.get("isExternalApp");
        if (externalAppString == 'true') {
            this.isExternalApp = true;
        }
        if (this.isExternalApp) {
            this.baseUrl = config.get('extApiUrl');
            if (!this.baseUrl) {
                config.load().then(() => {
                    this.baseUrl = config.config['extApiUrl'];
                });
            }
        } else {
            this.baseUrl = config.get('apiUrl');
            if (!this.baseUrl) {
                config.load().then(() => {
                    this.baseUrl = config.config['apiUrl'];
                });
            }
        }
    }

    callBlockUI(isBlockedUI: boolean) {
        this.CheckForblockUI(isBlockedUI);
    }

    createAuthorizationHeader(headers: HttpHeaders) {
        headers.append('Authorization', this.sessionService.getItem('id_token'));
        headers.append('SecretKey', this.generateAntiForgeryToken());
        headers.append('Content-Type', 'application/json');
        headers.append('Accept', 'application/json');
    }

    createAuthHttpHeader(headers: HttpHeaders): HttpHeaders {
        headers = headers.append('Authorization', this.sessionService.getItem('id_token'))
            .append('Expires', '0').append('SecretKey', this.generateAntiForgeryToken())
            .append('Content-Type', 'application/json').append('Accept', 'application/json');
        return headers;
    }

    uploadFile(file: any, invoiceStudyId: any, UserId: any, uploadImgUrl: any, apiurl: any): Observable<any> {
        this.CheckForblockUI(true);
        var userId = this.sessionService.getItem('loggedInUserId');
        return Observable.create(observer => {
            let xhr: XMLHttpRequest = new XMLHttpRequest();
            let formData = new FormData();
            formData.append("file", file, file.name);
            formData.append("invoiceStudyId", invoiceStudyId);
            formData.append("userId", UserId)
            xhr.onreadystatechange = () => {
                if (xhr.readyState === 4) {
                    if (xhr.status === 200) {
                        observer.next(JSON.parse(xhr.response));
                        observer.complete();
                    } else {
                        observer.error(xhr.response);
                    }
                }
            };
            xhr.open('POST', uploadImgUrl + apiurl, true);
            xhr.send(formData);
        }).pipe(map(
            (response: any) => this.handleResponse(response),
            (error: any) => this.handleResponseError(error)
        )).pipe(catchError(err => this.handleResponseError(err)));
    }

    uploadFileByRecordReference(file: any, recordReferenceId: any, UserId: any, uploadImgUrl: any, apiurl: any): Observable<any> {
        this.CheckForblockUI(true);
        var userId = this.sessionService.getItem('loggedInUserId');
        return Observable.create(observer => {
            let xhr: XMLHttpRequest = new XMLHttpRequest();
            let formData = new FormData();
            formData.append("file", file, file.name);
            formData.append("recordReferenceId", recordReferenceId);
            formData.append("userId", UserId)
            xhr.onreadystatechange = () => {
                if (xhr.readyState === 4) {
                    if (xhr.status === 200) {
                        observer.next(JSON.parse(xhr.response));
                        observer.complete();
                    } else {
                        observer.error(xhr.response);
                    }
                }
            };
            xhr.open('POST', uploadImgUrl + apiurl, true);
            xhr.send(formData);
        }).pipe(map(
            (response: any) => this.handleResponse(response),
            (error: any) => this.handleResponseError(error)
        )).pipe(catchError(err => this.handleResponseError(err)));
    }

    get(url): Observable<any> {
        this.CheckForblockUI(true);
        let headers = new HttpHeaders();
        headers = this.createAuthHttpHeader(headers);
        return this.http.get(this.baseUrl + url, {
            headers: headers
        }).pipe(map(
            (response: any) => this.handleResponse(response),
            (error: any) => this.handleResponseError(error)
        )).pipe(catchError(err => this.handleResponseError(err)));
    }

    getWithResponseTypeBlob(url): Observable<any> {
        this.CheckForblockUI(true);
        let headers = new HttpHeaders();
        headers = headers.set('Authorization', this.sessionService.getItem('id_token'))
            .set('Expires', '0')
            .set('SecretKey', this.generateAntiForgeryToken())
            .set('Content-Type', 'application/json')
            .set('Accept', 'application/json');
        return this.http.get(this.baseUrl + url, {
            responseType: 'blob',
            headers: headers
        }).pipe(map(
            (response: any) => this.handleResponse(response),
            (error: any) => this.handleResponseError(error)
        )).pipe(catchError(err => this.handleResponseError(err)));
    }

    getWithResponseTypeBlobWithObject(url): Observable<any> {
        this.CheckForblockUI(true);
        let headers = new HttpHeaders();
        headers = headers.set('Authorization', this.sessionService.getItem('id_token'))
            .set('Expires', '0')
            .set('SecretKey', this.generateAntiForgeryToken())
            .set('Content-Type', 'application/json')
            .set('Accept', 'application/json');
        return this.http.get(this.baseUrl + url, {
            responseType: 'blob',
            observe: 'response',
            headers: headers
        }).pipe(map(
            (response: any) => this.handleResponse(response),
            (error: any) => this.handleResponseError(error)
        )).pipe(catchError(err => this.handleResponseError(err)));
    }

    postWithResponseTypeBlob(url, data): Observable<any> {
        this.CheckForblockUI(true);
        let headers = new HttpHeaders();
        headers = headers.set('Authorization', this.sessionService.getItem('id_token'))
            .set('Expires', '0')
            .set('SecretKey', this.generateAntiForgeryToken())
            .set('Content-Type', 'application/json')
            .set('Accept', 'application/json');
        return this.http.post(this.baseUrl + url, data, {
            responseType: 'blob',
            headers: headers
        }).pipe(map(
            (response: any) => this.handleResponse(response),
            (error: any) => this.handleResponseError(error)
        )).pipe(catchError(err => this.handleResponseError(err)));
    }

    postWithResponseTypeBlobWithObject(url, data): Observable<any> {
        this.CheckForblockUI(true);
        let headers = new HttpHeaders();
        headers = headers.set('Authorization', this.sessionService.getItem('id_token'))
            .set('Expires', '0')
            .set('SecretKey', this.generateAntiForgeryToken())
            .set('Content-Type', 'application/json')
            .set('Accept', 'application/json');
        return this.http.post(this.baseUrl + url, data, {
            responseType: 'blob',
            observe: 'response',
            headers: headers
        }).pipe(map(
            (response: any) => this.handleResponse(response),
            (error: any) => this.handleResponseError(error)
        )).pipe(catchError(err => this.handleResponseError(err)));
    }

    getWithNoBlockUI(url): Observable<any> {
        let headers = new HttpHeaders();
        headers = this.createAuthHttpHeader(headers);
        return this.http.get(this.baseUrl + url, {
            headers: headers
        }).pipe(map(
            (response: any) => this.handleResponseWithNoBlockUI(response),
            (error: any) => this.handleResponseError(error)
        )).pipe(catchError(err => this.handleResponseError(err)));
    }

    post(url, data): Observable<any> {
        this.CheckForblockUI(true);
        let headers = new HttpHeaders();
        headers = this.createAuthHttpHeader(headers);
        return this.http.post(this.baseUrl + url, data, {
            headers: headers
        }).pipe(map(
            (response: any) => this.handleResponse(response),
            (error: any) => this.handleResponseError(error)
        )).pipe(catchError(err => this.handleResponseError(err)));
    }

    postWithHeader(url, data, headers: any): Observable<any> {
        this.CheckForblockUI(true);
        this.createAuthorizationHeader(headers);
        return this.http.post(this.baseUrl + url, data, {
            headers: headers
        }).pipe(map(
            (response: any) => this.handleResponse(response),
            (error: any) => this.handleResponseError(error)
        )).pipe(catchError(err => this.handleResponseError(err)));
    }

    postWithXhr(url, data: FormData): Observable<any> {
        return Observable.create(observer => {
            let xhr: XMLHttpRequest = new XMLHttpRequest();
            xhr.onreadystatechange = () => {
                if (xhr.readyState === 4) {
                    if (xhr.status === 200) {
                        observer.next(JSON.parse(xhr.response));
                        observer.complete();
                    }
                    else {
                        observer.error(xhr.response);
                    }
                }
            };
            let headers = new HttpHeaders();
            this.createAuthorizationHeader(headers);
            xhr.open('POST', url, true);
            xhr.setRequestHeader('Authorization', this.sessionService.getItem('id_token'))
            xhr.setRequestHeader('SecretKey', this.generateAntiForgeryToken());
            xhr.send(data);
        });
    }

    postWithXhrWithBlockUI(url, data: FormData): Observable<any> {
        this.CheckForblockUI(true);
        return Observable.create(observer => {
            let xhr: XMLHttpRequest = new XMLHttpRequest();
            xhr.onreadystatechange = () => {
                if (xhr.readyState === 4) {
                    if (xhr.status === 200) {
                        observer.next(JSON.parse(xhr.response));
                        observer.complete();
                    }
                    else {
                        observer.error(xhr.response);
                    }
                }
            };
            let headers = new HttpHeaders();
            this.createAuthorizationHeader(headers);
            xhr.open('POST', url, true);
            xhr.setRequestHeader('Authorization', this.sessionService.getItem('id_token'))
            xhr.setRequestHeader('SecretKey', this.generateAntiForgeryToken());
            xhr.send(data);
        }).pipe(map(
            (response: any) => this.handleResponse(response),
            (error: any) => this.handleResponseError(error)
        )).pipe(catchError(err => this.handleResponseError(err)));
    }

    postWithNoBlockUI(url, data): Observable<any> {
        this.CheckForblockUI(false);
        let headers = new HttpHeaders();
        this.createAuthorizationHeader(headers);
        return this.http.post(this.baseUrl + url, data, {
            headers: headers
        }).pipe(map(
            (response: any) => this.handleResponseWithNoBlockUI(response),
            (error: any) => this.handleResponseError(error)
        )).pipe(catchError(err => this.handleResponseError(err)));
    }

    handleResponse(response: Response): any {
        this.CheckForblockUI(false);
        return response;
    }

    handleResponseWithNoBlockUI(response: Response): any {
        return response;
    }

    handleResponseError(error: Response): any {
        this.CheckForblockUI(false);
        return throwError(() => error || 'Server error');;
    }

    CheckForblockUI(blockUI: boolean) {
        this.blockUIService.blockUIEvent.emit({
            value: blockUI
        });
    }

    defaultPost(url, data): Observable<any> {
        let headers = new HttpHeaders();
        return this.http.post(this.baseUrl + url, data, {
            headers: headers
        }).pipe(map(
            (response: any) => this.handleResponse(response),
            (error: any) => this.handleResponseError(error)
        )).pipe(catchError(err => this.handleResponseError(err)));
    }

    defaultGet(url): Observable<any> {
        let headers = new HttpHeaders();
        headers.append('Expires', '0');
        return this.http.get(this.baseUrl + url, {
            headers: headers
        }).pipe(map(
            (response: any) => this.handleResponse(response),
            (error: any) => this.handleResponseError(error)
        )).pipe(catchError(err => this.handleResponseError(err)));
    }

    generateAntiForgeryToken() {
        let currentTimeStamp = new Date().getTime().toString();
        let bearertoken = this.sessionService.getItem('id_token');
        let newguid = Utils.createGuid();
        let encryptedHash = CryptoJS.HmacSHA256(AntiForgeryTokenConfig.AntiForgeryTokenProperies.Token + bearertoken + newguid + currentTimeStamp, (bearertoken + newguid + currentTimeStamp));
        let encryptedKey = CryptoJS.enc.Base64.stringify(encryptedHash);
        var secretKey = CryptoJS.AES.encrypt(CryptoJS.enc.Utf8.parse(encryptedKey + AntiForgeryTokenConfig.AntiForgeryTokenProperies.Separator + (bearertoken + newguid + currentTimeStamp)
            + AntiForgeryTokenConfig.AntiForgeryTokenProperies.Separator + currentTimeStamp
            + AntiForgeryTokenConfig.AntiForgeryTokenProperies.Separator + bearertoken),
            this.key, {
            keySize: 128 / 8,
            iv: this.iv,
            mode: CryptoJS.mode.CBC,
            padding: CryptoJS.pad.Pkcs7
        });
        return secretKey.toString();
    }
}